/**
 * STOMP event handlers for chat realtime updates.
 */
import apiService from "../service/apiservice/apiService";
import {
  normalizeSocketMessage,
  upsertMessageInRoomMap,
  upsertMessageReactionInRoomMap,
} from "../experience/chat/chatMessageUtils";
import { getChatRoomId } from "../experience/chat/chatRoomUtils";
import {
  chatCommunicationPageActiveState,
  chatCurrentUserIdState,
  chatMessagesByRoomState,
  chatNotificationsState,
  chatRoomsState,
  chatTypingUsersState,
  chatUnreadCountsState,
  selectedChatRoomIdState,
} from "../experience/chat/state/chatState";
import { TYPING_INDICATOR_TIMEOUT_MS } from "./socketConfig";

export const SOCKET_EVENT_TYPES = {
  NEW_MESSAGE: "NEW_MESSAGE",
  ROOM_CREATED: "ROOM_CREATED",
  ROOM_UPDATED: "ROOM_UPDATED",
  ROOM_REMOVED: "ROOM_REMOVED",
  UNREAD_UPDATED: "UNREAD_UPDATED",
  MESSAGE: "MESSAGE",
  TYPING: "TYPING",
  READ_RECEIPT: "READ_RECEIPT",
  GROUP_UPDATED: "GROUP_UPDATED",
  REACTION: "REACTION",
  MESSAGE_REACTION: "MESSAGE_REACTION",
  REACTION_UPDATED: "REACTION_UPDATED",
  MESSAGE_REACTION_UPDATED: "MESSAGE_REACTION_UPDATED",
};

const REACTION_EVENT_TYPES = new Set([
  SOCKET_EVENT_TYPES.REACTION,
  SOCKET_EVENT_TYPES.MESSAGE_REACTION,
  SOCKET_EVENT_TYPES.REACTION_UPDATED,
  SOCKET_EVENT_TYPES.MESSAGE_REACTION_UPDATED,
  "MESSAGE_REACTIONS_UPDATED",
]);

const parsePayload = (rawBody) => {
  if (!rawBody) {
    return null;
  }

  try {
    return typeof rawBody === "string" ? JSON.parse(rawBody) : rawBody;
  } catch {
    return null;
  }
};

const getEventType = (payload) => {
  const type = payload?.type || payload?.eventType || payload?.event;
  return type ? String(type).toUpperCase() : null;
};

const extractRoomIdFromKeywords = (source) => {
  if (!source) {
    return "";
  }

  const keywords = source.keywords || source.keyword;
  const list = Array.isArray(keywords) ? keywords : keywords ? [keywords] : [];

  for (const entry of list) {
    const keyword =
      typeof entry === "string" ? entry : entry?.keyword || entry?.key || "";
    if (String(keyword).startsWith("room_")) {
      return String(keyword).replace(/^room_/, "");
    }
  }

  return "";
};

const getRoomIdFromPayload = (payload) => {
  if (!payload) {
    return "";
  }

  const directId =
    payload.roomId ??
    payload.room?.roomId ??
    payload.communicationRoomId ??
    payload.groupId;

  if (directId !== undefined && directId !== null && directId !== "") {
    return String(directId);
  }

  return (
    extractRoomIdFromKeywords(payload) ||
    extractRoomIdFromKeywords(payload.message) ||
    ""
  );
};

const isRoomActivelyViewed = (get, roomId) => {
  if (!roomId) {
    return false;
  }

  const isCommunicationPageActive = get(chatCommunicationPageActiveState);
  const selectedRoomId = get(selectedChatRoomIdState);

  return (
    isCommunicationPageActive &&
    String(selectedRoomId) === String(roomId)
  );
};

const buildLastMessagePreview = (messagePayload) => {
  const rawMessage = messagePayload?.message;
  if (!rawMessage) {
    return null;
  }

  try {
    const parsed =
      typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
    return JSON.stringify({
      chatMessage: parsed?.chatMessage || parsed?.message || "",
    });
  } catch {
    return null;
  }
};

const upsertRoomMessage = (set, roomId, normalizedMessage) => {
  set(chatMessagesByRoomState, (prev) =>
    upsertMessageInRoomMap(prev, roomId, normalizedMessage),
  );
};

const moveRoomToTop = (set, roomId, previewUpdate = {}) => {
  set(chatRoomsState, (rooms) => {
    const index = rooms.findIndex(
      (room) => getChatRoomId(room) === String(roomId),
    );

    if (index === -1) {
      return rooms;
    }

    const updatedRoom = {
      ...rooms[index],
      ...previewUpdate,
      updatedAt: previewUpdate.updatedAt || Date.now(),
    };

    const nextRooms = [...rooms];
    nextRooms.splice(index, 1);
    nextRooms.unshift(updatedRoom);
    return nextRooms;
  });
};

const setUnreadCount = (set, roomId, count) => {
  if (!roomId) {
    return;
  }

  set(chatUnreadCountsState, (prev) => ({
    ...prev,
    [String(roomId)]: Math.max(0, Number(count) || 0),
  }));
};

const incrementUnreadCount = (set, get, roomId) => {
  const currentUnread = get(chatUnreadCountsState)[String(roomId)] || 0;
  setUnreadCount(set, roomId, currentUnread + 1);
};

const addNotification = (set, payload, roomId, currentUserId) => {
  const messagePayload = payload?.message || payload;
  const senderId = messagePayload?.sender;
  if (String(senderId) === String(currentUserId)) {
    return;
  }

  const rooms = payload?._roomsSnapshot || [];
  const room = rooms.find((item) => getChatRoomId(item) === String(roomId));
  const normalized = normalizeSocketMessage(messagePayload, room);

  if (!normalized?.content?.trim()) {
    return;
  }

  set(chatNotificationsState, (prev) => {
    const notificationId = `${roomId}-${normalized.messageId}`;
    if (prev.some((item) => item.id === notificationId)) {
      return prev;
    }

    return [
      {
        id: notificationId,
        roomId: String(roomId),
        senderName: normalized.senderName,
        content: normalized.content,
        timestamp: normalized.timestamp,
      },
      ...prev,
    ].slice(0, 50);
  });
};

const getMessageIdFromPayload = (payload) => {
  const messageId =
    payload?.messageId ??
    payload?.message?.id ??
    payload?.message?.messageId;

  if (messageId === undefined || messageId === null || messageId === "") {
    return "";
  }

  return String(messageId);
};

const getReactionValueFromPayload = (payload) => {
  if (payload?.removed === true || payload?.isRemoved === true) {
    return null;
  }

  // Backend REACTION events use `value` (emoji or null when removed).
  if (Object.hasOwn(payload, "value")) {
    return payload.value;
  }

  if (Object.hasOwn(payload, "reaction")) {
    return payload.reaction;
  }

  if (Object.hasOwn(payload, "emoji")) {
    return payload.emoji;
  }

  return undefined;
};

const handleMessageReactionEvent = (payload, context, explicitRoomId) => {
  const { get, set } = context;
  const roomId = explicitRoomId || getRoomIdFromPayload(payload);
  const messageId = getMessageIdFromPayload(payload);

  if (!roomId) {
    return;
  }

  const rooms = get(chatRoomsState);
  const room = rooms.find((item) => getChatRoomId(item) === String(roomId));
  const userIdentifier =
    payload?.actorId ??
    payload?.userIdentifier ??
    payload?.userId ??
    payload?.recipient ??
    payload?.sender;
  const reactionValue = getReactionValueFromPayload(payload);

  if (messageId && userIdentifier && reactionValue !== undefined) {
    const userName =
      payload?.userName ??
      payload?.userDisplayName ??
      payload?.senderName ??
      userIdentifier;

    set(chatMessagesByRoomState, (prev) =>
      upsertMessageReactionInRoomMap(
        prev,
        String(roomId),
        messageId,
        userIdentifier,
        reactionValue,
        userName,
      ),
    );
    return;
  }

  const messagePayload = payload?.message;
  if (messagePayload && typeof messagePayload === "object") {
    const normalized = normalizeSocketMessage(messagePayload, room);
    if (normalized?.messageId) {
      set(chatMessagesByRoomState, (prev) =>
        upsertMessageInRoomMap(prev, String(roomId), normalized),
      );
    }
  }
};

const mergeChatRoomUpdate = (set, updatedRoom) => {
  if (!updatedRoom?.roomId) {
    return false;
  }

  const roomId = String(updatedRoom.roomId);

  set(chatRoomsState, (rooms) => {
    const exists = rooms.some((room) => getChatRoomId(room) === roomId);

    if (!exists) {
      return [...rooms, updatedRoom];
    }

    return rooms.map((room) =>
      getChatRoomId(room) === roomId ? { ...room, ...updatedRoom } : room,
    );
  });

  return true;
};

const markCurrentUserInactiveInRoom = (set, roomId, userIdentifier) => {
  if (!roomId || !userIdentifier) {
    return false;
  }

  let updated = false;

  set(chatRoomsState, (rooms) =>
    rooms.map((room) => {
      if (getChatRoomId(room) !== String(roomId)) {
        return room;
      }

      if (!Array.isArray(room.members)) {
        return room;
      }

      updated = true;
      return {
        ...room,
        members: room.members.map((member) =>
          String(member?.userIdentifier) === String(userIdentifier)
            ? { ...member, activeInRoom: false }
            : member,
        ),
      };
    }),
  );

  return updated;
};

const removeRoomFromState = (set, roomId) => {
  if (!roomId) {
    return;
  }

  set(chatRoomsState, (rooms) =>
    rooms.filter((room) => getChatRoomId(room) !== String(roomId)),
  );
  set(chatUnreadCountsState, (prev) => {
    const next = { ...prev };
    delete next[String(roomId)];
    return next;
  });
};

const handleRoomRemovedEvent = (payload, context) => {
  const { get, set, refreshRooms, userIdentifier } = context;
  const roomId = getRoomIdFromPayload(payload);
  const currentUserId = userIdentifier || get(chatCurrentUserIdState);

  if (!roomId) {
    return;
  }

  if (mergeChatRoomUpdate(set, payload?.room)) {
    return;
  }

  if (payload?.hardDelete === true) {
    removeRoomFromState(set, roomId);
    return;
  }

  const existingRoom = get(chatRoomsState).find(
    (room) => getChatRoomId(room) === String(roomId),
  );

  if (existingRoom) {
    markCurrentUserInactiveInRoom(set, roomId, currentUserId);
  }

  // Re-fetch so inactive memberships stay visible, matching GET /rooms.
  refreshRooms?.();
};

const handleIncomingUserMessage = (payload, context) => {
  const { get, set, userIdentifier } = context;
  const roomId = getRoomIdFromPayload(payload);

  if (!roomId) {
    return;
  }

  const messagePayload = payload?.message || payload;
  const senderId = messagePayload?.sender;

  if (String(senderId) === String(userIdentifier)) {
    return;
  }

  const preview = buildLastMessagePreview(messagePayload);
  const timestamp =
    messagePayload?.createdAt ||
    messagePayload?.updatedAt ||
    Date.now();

  moveRoomToTop(set, roomId, {
    lastMessage: preview || undefined,
    lastMessageAt: timestamp,
  });

  const isViewingRoom = isRoomActivelyViewed(get, roomId);

  if (typeof payload?.unreadCount === "number") {
    if (!isViewingRoom) {
      setUnreadCount(set, roomId, payload.unreadCount);
    }
  } else if (!isViewingRoom) {
    incrementUnreadCount(set, get, roomId);
  }

  const rooms = get(chatRoomsState);
  const room = rooms.find((item) => getChatRoomId(item) === String(roomId));
  const normalized = normalizeSocketMessage(messagePayload, room);

  if (!normalized) {
    return;
  }

  if (isViewingRoom) {
    upsertRoomMessage(set, String(roomId), normalized);
  } else {
    addNotification(
      set,
      { ...payload, _roomsSnapshot: rooms },
      roomId,
      userIdentifier,
    );
  }
};

const scheduleTypingExpiry = (set, roomId, userId) => {
  setTimeout(() => {
    set(chatTypingUsersState, (prev) => {
      const roomTypers = prev[String(roomId)] || [];
      const nextTypers = roomTypers.filter(
        (item) => String(item.userId) !== String(userId),
      );

      if (nextTypers.length === roomTypers.length) {
        return prev;
      }

      return {
        ...prev,
        [String(roomId)]: nextTypers,
      };
    });
  }, TYPING_INDICATOR_TIMEOUT_MS);
};

export const createRefreshChatRooms =
  (organizationIdentifier, userIdentifier) => async (set) => {
    if (!organizationIdentifier || !userIdentifier) {
      return [];
    }

    const response = await apiService.get(
      "niramayafoundation/api/v1/communication/rooms",
      {
        recipient: userIdentifier,
        organizationIdentifier,
      },
    );

    if (Array.isArray(response)) {
      set(chatRoomsState, response);
      return response;
    }

    return [];
  };

export const syncUnreadCountsFromRooms = (rooms, set, get) => {
  if (!Array.isArray(rooms) || !rooms.length) {
    return;
  }

  set(chatUnreadCountsState, (prev) => {
    const next = { ...prev };

    rooms.forEach((room) => {
      const roomId = getChatRoomId(room);
      const unread =
        room?.unreadCount ??
        room?.unreadMessageCount ??
        room?.unread ??
        null;

      if (unread === null || unread === undefined) {
        return;
      }

      if (isRoomActivelyViewed(get, roomId)) {
        delete next[roomId];
        return;
      }

      next[roomId] = Math.max(0, Number(unread) || 0);
    });

    return next;
  });
};

export const bootstrapChatOnSocketConnect = async (
  organizationIdentifier,
  userIdentifier,
  set,
  get,
) => {
  const rooms = await createRefreshChatRooms(
    organizationIdentifier,
    userIdentifier,
  )(set);

  syncUnreadCountsFromRooms(rooms, set, get);
};

export const handleUserTopicEvent = (rawBody, context) => {
  const payload = parsePayload(rawBody);
  if (!payload) {
    return;
  }

  const eventType = getEventType(payload);
  const { get, set, refreshRooms } = context;
  const roomId = getRoomIdFromPayload(payload);

  switch (eventType) {
    case SOCKET_EVENT_TYPES.NEW_MESSAGE:
    case SOCKET_EVENT_TYPES.MESSAGE:
      handleIncomingUserMessage(payload, context);
      break;

    case SOCKET_EVENT_TYPES.ROOM_CREATED:
      refreshRooms?.();
      break;

    case SOCKET_EVENT_TYPES.ROOM_UPDATED: {
      if (!mergeChatRoomUpdate(set, payload?.room)) {
        refreshRooms?.();
      }
      break;
    }

    case SOCKET_EVENT_TYPES.ROOM_REMOVED:
      handleRoomRemovedEvent(payload, context);
      break;

    case SOCKET_EVENT_TYPES.UNREAD_UPDATED:
      if (roomId && typeof payload?.unreadCount === "number") {
        if (!isRoomActivelyViewed(get, roomId)) {
          setUnreadCount(set, roomId, payload.unreadCount);
        }
      }
      break;

    case SOCKET_EVENT_TYPES.REACTION:
    case SOCKET_EVENT_TYPES.MESSAGE_REACTION:
    case SOCKET_EVENT_TYPES.REACTION_UPDATED:
    case SOCKET_EVENT_TYPES.MESSAGE_REACTION_UPDATED:
      handleMessageReactionEvent(payload, context, roomId);
      break;

    default:
      if (REACTION_EVENT_TYPES.has(eventType)) {
        handleMessageReactionEvent(payload, context, roomId);
      }
      break;
  }
};

export const handleRoomTopicEvent = (rawBody, roomId, context) => {
  const payload = parsePayload(rawBody);
  if (!payload || !roomId) {
    return;
  }

  const eventType = getEventType(payload);
  const { get, set, refreshRooms } = context;
  const rooms = get(chatRoomsState);
  const room = rooms.find((item) => getChatRoomId(item) === String(roomId));

  switch (eventType) {
    case SOCKET_EVENT_TYPES.MESSAGE: {
      const messagePayload = payload?.message || payload;
      const normalized = normalizeSocketMessage(messagePayload, room);

      if (normalized) {
        upsertRoomMessage(set, String(roomId), normalized);

        const preview = buildLastMessagePreview(messagePayload);
        moveRoomToTop(set, roomId, {
          lastMessage: preview || undefined,
          lastMessageAt: normalized.timestamp,
        });
      }
      break;
    }

    case SOCKET_EVENT_TYPES.TYPING: {
      const typingUserId = payload?.userIdentifier || payload?.sender;
      const typingUserName =
        payload?.userName || payload?.senderName || "Someone";

      if (!typingUserId) {
        break;
      }

      const currentUserId = get(chatCurrentUserIdState);
      if (String(typingUserId) === String(currentUserId)) {
        break;
      }

      set(chatTypingUsersState, (prev) => {
        const roomTypers = prev[String(roomId)] || [];
        const withoutUser = roomTypers.filter(
          (item) => String(item.userId) !== String(typingUserId),
        );

        return {
          ...prev,
          [String(roomId)]: [
            ...withoutUser,
            {
              userId: String(typingUserId),
              userName: typingUserName,
              expiresAt: Date.now() + TYPING_INDICATOR_TIMEOUT_MS,
            },
          ],
        };
      });

      scheduleTypingExpiry(set, roomId, typingUserId);
      break;
    }

    case SOCKET_EVENT_TYPES.READ_RECEIPT: {
      const messageId = String(payload?.messageId ?? "");
      const readBy = payload?.readBy || payload?.userIdentifier;

      if (!messageId) {
        break;
      }

      set(chatMessagesByRoomState, (prev) => {
        const roomMessages = prev[String(roomId)];
        if (!roomMessages?.length) {
          return prev;
        }

        return {
          ...prev,
          [String(roomId)]: roomMessages.map((message) =>
            message.messageId === messageId
              ? {
                  ...message,
                  readBy: [...(message.readBy || []), readBy].filter(Boolean),
                }
              : message,
          ),
        };
      });
      break;
    }

    case SOCKET_EVENT_TYPES.GROUP_UPDATED: {
      if (!mergeChatRoomUpdate(set, payload?.room)) {
        refreshRooms?.();
      }
      break;
    }

    case SOCKET_EVENT_TYPES.REACTION:
    case SOCKET_EVENT_TYPES.MESSAGE_REACTION:
    case SOCKET_EVENT_TYPES.REACTION_UPDATED:
    case SOCKET_EVENT_TYPES.MESSAGE_REACTION_UPDATED:
      handleMessageReactionEvent(payload, context, roomId);
      break;

    default:
      if (REACTION_EVENT_TYPES.has(eventType)) {
        handleMessageReactionEvent(payload, context, roomId);
      }
      break;
  }
};

export const resetChatSocketRecoilState = (set) => {
  set(chatUnreadCountsState, {});
  set(chatNotificationsState, []);
  set(chatTypingUsersState, {});
};
